import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, Subject, tap } from 'rxjs';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { DeviceServiceStore } from 'src/app/stores/component/device-service-store';
import { DevicePollution, DeviceWear, ICreateClientServiceRequest, ICreateServiceRequest, IService, IServiceDetailsResponse, IUpdateClientServiceRequest, IUpdateServiceRequest, ServiceStatus } from '../../models/service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-device-detail-services',
    templateUrl: './device-detail-services.component.html',
    styleUrl: './device-detail-services.component.scss',
    standalone: false
})
export class DeviceDetailServicesComponent {
  @Input() isAdmin;
  @Input() deviceId;

  vm$: Observable<any>;
  editingService: IServiceDetailsResponse | null = null;
  editingClientService: IServiceDetailsResponse | null = null;
  servicesOpened = false;
  clientServicesOpened = false;
  baseUrl: string;
  openService$: Observable<boolean>;
  serviceStatus: Array<{ text: string, value: ServiceStatus }>;
  deviceWear: Array<{ text: string, value: DeviceWear }>;
  devicePollution: Array<{ text: string, value: DevicePollution }>;
  serviceImageCount: number = 0;

  public serviceForm: UntypedFormGroup;
  public clientServiceForm: UntypedFormGroup;
  public deletionDialogOpened = false;
  public deletionClientDialogOpened = false;

  constructor(
    private store: Store<AppStateInterface>,
    private translocoService: TranslocoService,
    private fb: FormBuilder,
    private deviceServiceStore: DeviceServiceStore,
    private permissionServie: PermissionStorageService,
    private router: Router
  ) { 
    this.vm$ = combineLatest([ 
      this.deviceServiceStore.deviceServices$,
      this.deviceServiceStore.loading$,
      this.deviceServiceStore.openService$
    ]).pipe(
      map(([
        services,
        loading,
        openService
      ]) => ({
        services,
        loading,
        openService
      })),
      tap(({ services }) => this.patchServicesToForm(services))
    );

    // Inizialise form
    this.serviceForm = this.fb.group({
      dateScheduled: new UntypedFormControl (null, {
        validators: Validators.required
      }),
      description: new UntypedFormControl(),
      dateExecuted: new UntypedFormControl(null),
      deviceWear: new UntypedFormControl(null),
      devicePollution: new UntypedFormControl(null),
      remark: new UntypedFormControl(null)
    });

    this.clientServiceForm = this.fb.group({
      description: new UntypedFormControl(),
      dateExecuted: new UntypedFormControl (null, {
        validators: Validators.required
      })
    });
  }

  ngOnInit() {
    this.deviceServiceStore.getServicesByDevice(this.deviceId);
    this.initEnums();

    this.baseUrl = this.router.url.split('/')[0] + '/revisions-admin/';
  }

  initEnums() {
    this.serviceStatus = Object.keys(ServiceStatus)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
          text: this.translocoService.translate(`revisions.detail.device.serviceStatusEnums.${key}`),
          value: ServiceStatus[key as keyof typeof ServiceStatus]
    }));

    this.deviceWear = Object.keys(DeviceWear)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
          text: this.translocoService.translate(`revisions.detail.device.deviceWearEnums.${key}`),
          value: DeviceWear[key as keyof typeof DeviceWear]
    }));

    this.devicePollution = Object.keys(DevicePollution)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
          text: this.translocoService.translate(`revisions.detail.device.devicePollutionEnums.${key}`),
          value: DevicePollution[key as keyof typeof DevicePollution]
    }));
  }

  get services(): FormArray {
    return this.serviceForm.get('services') as FormArray;
  }

  get clientServices(): FormArray {
    return this.clientServiceForm.get('clientServices') as FormArray;
  }

  public getDeviceWearByValue(value: DeviceWear): { text: string; value: DeviceWear } | undefined {
    return this.deviceWear.find(item => item.value === value);
  }

  public getDevicePollutionByValue(value: DevicePollution): { text: string; value: DevicePollution } | undefined {
    return this.devicePollution.find(item => item.value === value);
  }

  public getStatusByValue(value: ServiceStatus): { text: string; value: ServiceStatus } | undefined {
    return this.serviceStatus.find(item => item.value === value);
  }

  public getImageCount(service: any) {
    return service?.serviceAttachments?.length;
  }
  
  // Method to patch services into the form
  private patchServicesToForm(services: IService[]): void {
    if (this.services) {
      this.services.clear();

      services.forEach(service => {
        this.services.push(this.fb.group({
          dateScheduled: [service.dateScheduled || null, Validators.required],
          description: [service.description || ''],
          dateExecuted: [service.dateExecuted || null],
          deviceWear: [service.deviceWear || null],
          devicePollution: [service.devicePollution || null]
        }));
      });
    }
  }
  
  editService(service: IServiceDetailsResponse): void {
      if (service.type === 20) {
          this.openClientServiceDialog(service);
      } else {
          this.openServiceDialog(service);
      }
  }

  removeService(): void {
    this.deviceServiceStore.removeServiceById(this.editingService.id);
    this.closeServiceDialog('no');
  }

  removeClientService(): void {
    this.deviceServiceStore.removeClientServiceById(this.editingClientService.id);
    this.closeClientServiceDialog('no');
  }

  addService(): void {
    this.services.push(
      this.fb.group({
        dateScheduled: [null],
        description: [''], 
        dateExecuted: [null],
        deviceWear: [null],
        devicePollution: [null]
      })
    );
  }  

  sanitizeCreationDate(inputdate: any) {
    if (!inputdate) {
      return "-";
    }

    const date = new Date(inputdate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  public openServiceDialog(service?: IServiceDetailsResponse): void {
    this.editingService = service || null;

    if (service) {
      // Populate the form with existing service data for editing
      this.serviceForm.patchValue({
        description: service.description,
        dateScheduled: service.dateScheduled ? new Date(service.dateScheduled) : null,
        dateExecuted: service.dateExecuted ? new Date(service.dateExecuted) : null,
        deviceWear: service.deviceWear,
        devicePollution: service.devicePollution,
        remark: service.remark
      });
    } else {
      // Reset form for creating a new service entry
      this.serviceForm.reset();
    }

    this.servicesOpened = true;
  }

  public closeServiceDialog(status: string): void {
    this.servicesOpened = false;
    this.serviceForm.reset();
  }

  public openClientServiceDialog(service?: IServiceDetailsResponse): void {
    this.editingClientService = service || null;

    if (service) {
      // Populate the form with existing service data for editing
      this.clientServiceForm.patchValue({
        description: service.description,
        dateExecuted: service.dateExecuted ? new Date(service.dateExecuted) : null,
      });
    } else {
      // Reset form for creating a new service entry
      this.clientServiceForm.reset();
    }

    this.clientServicesOpened = true;
  }

  public closeClientServiceDialog(status: string): void {
    this.clientServicesOpened = false;
    this.clientServiceForm.reset();
  }

  submitServiceCreation() {
    if (this.editingService) {
      // Edit existing service
      const updateRequest: IUpdateServiceRequest = {
        id: this.editingService.id,
        description: this.serviceForm.value.description,
        dateExecuted: this.serviceForm.value.dateExecuted,
        dateScheduled: this.serviceForm.value.dateScheduled,
        deviceWear: this.serviceForm.value.deviceWear,
        devicePollution: this.serviceForm.value.devicePollution,
        remark: this.serviceForm.value.remark
      };
      this.deviceServiceStore.updateServiceById(updateRequest);
    } else {
      // Create new service
      const request: ICreateServiceRequest = {
        description: this.serviceForm.value.description,
        deviceId: this.deviceId,
        dateScheduled: this.serviceForm.value.dateScheduled,
        dateExecuted: this.serviceForm.value.dateExecuted,
        status: ServiceStatus.Open,
      };
      this.deviceServiceStore.addService(request);
    }
    this.closeServiceDialog('no');
  }

  submitClientServiceCreation() {
    if (this.editingClientService) {
      // Edit existing service
      const updateRequest: IUpdateClientServiceRequest = {
        id: this.editingClientService.id,
        description: this.clientServiceForm.value.description,
        dateExecuted: this.clientServiceForm.value.dateExecuted,
      };
      this.deviceServiceStore.updateClientServiceById(updateRequest);
    } else {
      // Create new service
      const request: ICreateClientServiceRequest = {
        description: this.clientServiceForm.value.description,
        deviceId: this.deviceId,
        dateExecuted: this.clientServiceForm.value.dateExecuted
      };
      this.deviceServiceStore.addClientService(request);
    }
    this.closeClientServiceDialog('no');
  }

  // delete confirmation dialog
  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete') {
      this.removeService();
    }
  }

  public deletionClientDialogClose(status: string): void {
    this.deletionClientDialogOpened = false;

    if(status == 'delete') {
      this.removeClientService();
    }
  }

  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }

  public deletionClientDialogOpen(): void {
    this.deletionClientDialogOpened = true;
  }
}
